import { Direction } from '@angular/cdk/bidi';
import { Overlay } from '@angular/cdk/overlay';
import { Component, ElementRef, Inject, OnInit, ViewChild } from '@angular/core';
import { tap } from 'rxjs';
import { PopUpComponent } from 'src/app/@shared/components/pop-ups/pop-up.component';
import { POPUP_DATA } from 'src/app/@shared/services/modal/modal.service';
import { ResizeService } from 'src/app/@shared/services/resize/resize.service';
import { SessionService } from 'src/app/@shared/services/session/session.service';
import { Rating } from 'src/app/modules/route-planner/interfaces/route-planning/rating.interface';
import { RatingService } from 'src/app/modules/route-planner/services/rating/rating.service';
import { RoutePlanningService } from 'src/app/modules/route-planner/services/route-planning/route-planning.service';
import { LanguageService } from 'src/app/@shared/services/language/language.service';
import { CLOSE, HOW_TO_RATE_STATION, HOW_TO_RATE_LINE, RATING_ERROR, RATING_SENT_SUCCESSFULLY, SEND } from 'src/app/modules/route-planner/constants/multi-language.constants';

@Component({
    selector: 'app-line-rating',
    templateUrl: './line-rating.component.html',
    styleUrls: ['./line-rating.component.scss'],
    standalone: false
})
export class LineRatingComponent extends PopUpComponent {
  submittedRating: boolean;
  errorSubmitting: boolean;
  selectedRating: number;

  direction: Direction
  closeText: string
  sendText: string
  ratingSentSuccessfullyText: string
  ratingErrorText: string
  howToRateStationText: string
  howToRateLineText: string

  /**
   * a11y: ref to the wrapping element around the close <mot-button> so we can
   * move keyboard focus there after submission (the "Send" button is removed
   * from the DOM when `submittedRating` flips to true, otherwise focus would
   * land on <body>, dropping the user out of the dialog's focus trap context).
   */
  @ViewChild('closeBtnWrapper', { read: ElementRef })
  private closeBtnWrapper?: ElementRef<HTMLElement>;

  constructor(
    routePlanningService: RoutePlanningService,
    resizeService: ResizeService,
    overlay: Overlay,

    @Inject(POPUP_DATA)
    public readonly data: { entityId: string, rating: Rating, entityType: number },

    private readonly sessionService: SessionService,
    private readonly ratingService: RatingService,
    private readonly languageService: LanguageService,
  ) {
    super(routePlanningService, resizeService, overlay);
  }

  override ngOnInit(): void {
    super.ngOnInit();
    this.selectedRating = this.data.rating.averageRating;
    this.initializeMultiLanguageTexts()
  }

  /**
   * a11y: SR-only message rendered in the live region (role="status"). The
   * decorative ✔ / ✖ are stripped so that screen readers don't announce the
   * Unicode glyph name ("heavy check mark") next to the message. The message
   * is empty while submission is in-flight or hasn't been triggered yet, so
   * the live region only speaks once the final state is reached.
   */
  get statusAnnouncement(): string {
    if (!this.submittedRating) {
      return '';
    }
    const raw = this.errorSubmitting
      ? this.ratingErrorText
      : this.ratingSentSuccessfullyText;
    return (raw ?? '').replace(/[✔✖]/g, '').trim();
  }

  handleSubmitRating() {
    this.sessionService.getSessionId().subscribe(sessionId => {
      this.submittedRating = true;
      this.ratingService.addRating({
        sessionId: sessionId,
        entityId: this.data.entityId.toString(),
        rating: this.selectedRating,
        type: this.data.entityType,
      })
        .subscribe({
          next: res => {
            this.data.rating = res?.data;
            this.moveFocusToCloseAfterSubmit();
          },
          error: (err) => {
            this.errorSubmitting = true
            this.moveFocusToCloseAfterSubmit();
          }
        })
    })
  }

  /**
   * a11y: after the Send button is swapped out for the disabled status button,
   * the previously focused element no longer exists in the DOM and focus
   * collapses to <body>. Move it to the Close button so the user stays inside
   * the dialog and the focus trap can keep them there.
   */
  private moveFocusToCloseAfterSubmit(): void {
    // Defer to next tick so the @if/@else has rebuilt the DOM.
    setTimeout(() => {
      const btn = this.closeBtnWrapper?.nativeElement.querySelector<HTMLButtonElement>('button');
      btn?.focus();
    }, 0);
  }

  initializeMultiLanguageTexts() {
    this.languageService.selectedLanguage$.subscribe(language => {
      this.direction = language.direction
      this.closeText = CLOSE[language.language]
      this.sendText = SEND[language.language]
      this.ratingSentSuccessfullyText = RATING_SENT_SUCCESSFULLY[language.language]
      this.ratingErrorText = RATING_ERROR[language.language]
      this.howToRateStationText = HOW_TO_RATE_STATION[language.language]
      this.howToRateLineText = HOW_TO_RATE_LINE[language.language]
    })
  }
}
